/**
 * Exception di applicazione.
 * Questa classe crea un oggetto ApplicationError per standardizzare ed estendere le eccezioni.
 *
 * Per utilizzarlo in un componente:
 * 1. Importarlo
 * 2. Per utilizzarlo usare throw new ApplicationError('exception message');
 */

import { ErrorHandler, inject, Injectable, Injector } from "@angular/core";
import { LoggerService } from "./logger.service";
import { TranslateService } from "../app/util/translate.service";
import { ClientAuditService } from "../app/services/client-audit.service";
import { ApplicationError } from "./exception.model";

// Le classi di errore vivono in exception.model.ts (modulo foglia, vedi nota lì) e vengono
// ri-esportate qui per compatibilità con gli import esistenti
export { ApplicationError, ClientError, HTTPError } from "./exception.model";

@Injectable({ providedIn: "root" })
export class GlobalError implements ErrorHandler {
  private injector = inject(Injector);

  constructor() { }

  /**
   *
   * Gestore centralizzato degli errori
   *
   * @param error
   */
  public handleError(error: unknown) {
    const logger = this.injector.get(LoggerService);
    // const alertService = this.injector.get(AlertService);
    const translate = this.injector.get(TranslateService);

    // Traccia l'errore nel sistema di audit client (silenzioso, non propaga errori)
    try {
      const clientAudit = this.injector.get(ClientAuditService);
      const errorMessage = error instanceof Error
        ? error.message
        : String(error);
      clientAudit.trackError(errorMessage);
    } catch {
      // Non propagare errori di audit client
    }

    // Gestione errori generici (non ApplicationError)
    if (!(error instanceof ApplicationError)) {
      logger.error("Generic error occurred:", String(error));
      return;
    }

    const {
      httpErrorCode, errorKey, messageFromError, applicationError, method, serviceName, name
    } = error;

    // NOTA: Rimossa navigazione automatica per evitare dipendenza circolare con Router
    // La gestione dell'errore 403 deve essere fatta nel componente che riceve l'errore

    if (httpErrorCode == 401) {
      logger.warn("Errore 401 - Unauthorized");
    } else if (httpErrorCode == 403) {
      logger.warn("Errore 403 - Forbidden");
    } else {
      /* Errore proveniente dalla risposta della chiamata **/
      const hasErrorCode = translate.has(httpErrorCode?.toString());
      /* Errore proveniente dalla risposta della chiamata **/
      const hasApplicationError = translate.has(applicationError);
      /* Errore proveniente dal server **/
      const hasHttpError = translate.has(errorKey);
      const appendix = `From ${serviceName || "Unknown"} -- Method ${method?.toUpperCase() || "Unknown"}`;

      if (hasErrorCode || hasApplicationError) {
        const param = typeof messageFromError === "string"
          ? messageFromError
          : Object.values(messageFromError ?? [])
            ?.join(", ");
        const translatedError = translate.instant<string>(applicationError && hasApplicationError
          ? applicationError
          : httpErrorCode?.toString() ?? "", { arg: { param: param } });

        // alertService.showError(translatedError);
        logger.error(translatedError, appendix);
      }

      if (hasHttpError && errorKey) {
        const translatedError = translate.instant<string>(errorKey);
        // alertService.showError(translatedError);
        logger.error(translatedError, appendix);
      }

      if (!hasHttpError && !hasErrorCode && !hasApplicationError) {
        // alertService.showError(name || "Unknown error");
        logger.error(name || "Unknown error", appendix);
      }
    }
  }
}
